import React, { useCallback, useState } from 'react';
import styles from './productList.module.scss';
import { adePage, commodityEmptyStateDefaultValues } from 'constants/constants';
import LoaderAnimation from 'shared/components/LoaderAnimation';
import COProductCard from 'shared/components/COProductCard';
import COAlertModal from 'shared/components/COAlertModal';
import { AllProductsProps } from 'enums/AllProductsEnums';
import { useSearchParams } from 'react-router-dom';
import {
  CommodityStatusParams,
  IN_REVIEW_ROUTE,
  inReviewStatus,
  livestockStageMapping,
  stageMapping,
  statusMapping,
  statusMappingWithStage,
} from 'views/FarmProductPage/constant';
import { CommodityList } from 'models/CommodityList/CommodityList.model';
import EmptyStateScope from 'shared/components/CategoryEmptyState';
import { CommodityTypes } from 'constants/FarmboxConstants/stage0.constants';
import { useRedirectBasedOnProductStatus } from 'shared/utils/redirectBasedonProductStatus';

export interface CommodityProductListsProps {
  currentTab: string;
  commodityLoading: boolean;
  commodityDataList?: CommodityList;
  handleDelete: (id: number, type: string) => Promise<void>;
}
const CommodityProductLists = (props: CommodityProductListsProps) => {
  const { currentTab, commodityLoading, commodityDataList, handleDelete } =
    props;

  const [searchParams] = useSearchParams();

  const redirectBasedOnProductStatus = useRedirectBasedOnProductStatus();

  const page = parseInt(
    searchParams.get(CommodityStatusParams.PAGE) ||
      CommodityStatusParams.PAGE_DEFAULT,
    10
  );

  const [showDeleteModalState, setDeleteModalState] = useState<boolean>(false);
  const [deleteCommodityID, setDeleteCommodityId] = useState<null | number>(
    null
  );
  const [deletedCommodityType, setDeletedCommodityType] = useState<string>('');
  const [isFarmCommodityModal, setIsFarmCommodityModal] =
    useState<boolean>(false);

  const mappedStatus = statusMapping[currentTab];
  const { TITLE, SUBTITLE } = commodityEmptyStateDefaultValues;

  const handleOpenDeleteModal = () => {
    setDeleteModalState(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalState(false);
  };

  const handleModalConfirm = async () => {
    if (deleteCommodityID !== null)
      await handleDelete(deleteCommodityID, deletedCommodityType);
    handleCloseDeleteModal();
    setDeleteCommodityId(null);
  };

  const onDeleteClick = useCallback((id?: number, type?: string) => {
    handleOpenDeleteModal();
    setDeleteCommodityId(id ?? null);
    setDeletedCommodityType(type ?? '');
  }, []);

  const handleOpenCreateCommodityModal = () => {
    setIsFarmCommodityModal(true);
  };

  const handleNavigateProductView = (
    productId?: number,
    stage?: number,
    status?: string,
    commodityType?: string
  ) => {
    const mappedStage =
      commodityType === CommodityTypes.LIVE_STOCK
        ? livestockStageMapping[stage ? Number(stage + 1) : 1]
        : stageMapping[stage ? Number(stage + 1) : 1];
    const mappedStatus =
      statusMappingWithStage[
        status === inReviewStatus ? IN_REVIEW_ROUTE : status ?? ''
      ];
    redirectBasedOnProductStatus({
      productId: productId ?? '',
      mappedStatus: mappedStatus ?? '',
      mappedStage: mappedStage ?? '',
      commodityType: commodityType ?? '',
      adePage,
    });
  };

  //Todo for future use
  const handleRedirection = () => {};

  return (
    <div className={styles.productListContainer}>
      {commodityLoading ? (
        <div className={styles.loaderAnimation}>
          <LoaderAnimation />
        </div>
      ) : (commodityDataList?.meta?.totalItems ?? 0) < 1 ? (
        <div className={styles.buttonContainer}>
          <div onClick={handleOpenCreateCommodityModal}>
            <EmptyStateScope
              title={TITLE}
              subString={SUBTITLE}
              handleRedirect={handleRedirection}
              commodityState
            />
          </div>
        </div>
      ) : (
        <div className={styles.commodityCardsContainer}>
          {commodityDataList?.commodities?.map(data => (
            <COProductCard
              key={data.commodityId}
              data={data}
              navigateProductOverview={handleNavigateProductView}
              calenderRow
              showStatus
              multiRowCard
              commodityCard
              showDownloadOptions={data.status}
              onDeleteClick={onDeleteClick.bind(
                null,
                data?.commodityId,
                data?.commodityType
              )}
            />
          ))}
        </div>
      )}
      <COAlertModal
        handleModalCancel={handleCloseDeleteModal}
        handleModalConfirm={handleModalConfirm}
        showModalState={showDeleteModalState}
        deleteModal
        heading={AllProductsProps.DELETE_COMMODITY}
        subHeading={AllProductsProps.DELETE_COMMODITY_SUBHEADING}
      />
      {/* Stage 0 modal Todo : Add stage 0 modal , for future use uncomment it */}
      {/* <FarmCommodity
        visible={isFarmCommodityModal}
        setVisible={setIsFarmCommodityModal}
        fetchCommodityList={fetchCommodityList}
      /> */}
    </div>
  );
};

export default CommodityProductLists;
